import { useQuery } from "@tanstack/react-query";
import { businessAccessApi } from "../api/businessAccessClient";
import { fetchCombinedAll, toDayParam } from "../api/businessAccessCombined";
import { BusinessAccess, BusinessAccessResponse } from "./useBusinessAccessInvitations";

export type DashboardFilterType = 'today' | 'week' | 'month' | 'ytd' | 'custom';

export type DashboardFilter = {
  timeRange?: DashboardFilterType;
  accessTypes?: number[];
  statuses?: number[];
  hasEvent?: boolean;
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
  host_user_id?: string; // Filter by host user (property managers)
  estate_id?: string; // Filter by estate
};

/**
 * Aplica los filtros del lado del cliente sobre los datos ya obtenidos del
 * backend. Extraída como función pura (sin dependencias de React Query) para
 * poder testearla directamente.
 */
export const applyDashboardFilters = (
  data: BusinessAccess[],
  filters: DashboardFilter
): BusinessAccess[] => {
  let filteredData = data;

  // SAM-217: apply the selected date range on the client (see note in
  // useDashboardData below). Filter on `start_date`, matching the field
  // used by the chart series.
  if (filters.startDate || filters.endDate) {
    const from = filters.startDate ? new Date(filters.startDate).getTime() : -Infinity;
    const to = filters.endDate ? new Date(filters.endDate).getTime() : Infinity;
    filteredData = filteredData.filter((item) => {
      const t = new Date(item.start_date).getTime();
      if (Number.isNaN(t)) return false;
      return t >= from && t <= to;
    });
  }

  // Filtrar por tipos de acceso
  if (filters.accessTypes && filters.accessTypes.length > 0) {
    filteredData = filteredData.filter(item =>
      filters.accessTypes!.includes(item.business_access_type_id)
    );
  }

  // Filtrar por estados
  if (filters.statuses && filters.statuses.length > 0) {
    filteredData = filteredData.filter(item =>
      filters.statuses!.includes(item.business_access_status_id)
    );
  }

  // Filtrar por presencia de evento
  if (filters.hasEvent !== undefined) {
    filteredData = filteredData.filter(item =>
      filters.hasEvent ? item.event_id !== null : item.event_id === null
    );
  }

  // Filtrar por término de búsqueda
  if (filters.searchTerm && filters.searchTerm.trim()) {
    const searchLower = filters.searchTerm.toLowerCase().trim();
    filteredData = filteredData.filter(item => {
      if (!item.main_guest) return false;
      const fullName = `${item.main_guest.name} ${item.main_guest.last_name}`.toLowerCase();
      return fullName.includes(searchLower);
    });
  }

  return filteredData;
};

/**
 * Who reads the Dashboard from /business_access/combined (SAM-1311 B1, SAM-1320 B8, SAM-1323 U39): the estate group
 * roles, the tenant Front Desk (its rows carry type_name/status_name, like its Passes list) and the tenant admin, whose passes are not only business passes. /business_access answered 404 "No data found"
 * and the Dashboard showed 0 with passes of today. The Xhinola admin stays on /business_access (its Dashboard
 * works, and the combined routes answer 403 for it, B9).
 */
export const dashboardReadsCombined = (p: { isAdmin?: boolean; isXhinolaAdmin?: boolean; isEstateGroupAdmin?: boolean; isEstateGroupFrontDesk?: boolean; isTenantFrontDesk?: boolean }): boolean =>
  Boolean(p.isEstateGroupAdmin || p.isEstateGroupFrontDesk || p.isTenantFrontDesk || (p.isAdmin && !p.isXhinolaAdmin));

/**
 * Hook personalizado para obtener datos del dashboard con filtros avanzados
 * @param filters Filtros para aplicar a la consulta
 * @returns Datos de la consulta
 */
/** `enabled`: false until the roles are known, so the first request is not the wrong endpoint (the 404 that came first in SAM-1311 QA). `combined`: read /business_access/combined (business passes and residential invitations) instead of /business_access (business passes only). */
export const useDashboardData = (filters: DashboardFilter, options: { combined?: boolean; enabled?: boolean } = {}) => {
  const combined = options.combined === true;
  // Construir los parámetros basados en los filtros
  const params: Record<string, string | number> = {};
  
  // Añadir límite de resultados
  params.limit = 10000;

  // NOTE (SAM-217): the date range is intentionally NOT sent as a server param.
  // The CRUD backend only understands the _gt/_lt/_date/_ilike/_search/_in
  // suffixes, so the previous start_date_gte/end_date_lte params were silently
  // ignored and every preset returned the full historical dataset. The range is
  // now applied on the client below, on the same `start_date` field the charts
  // group by, so KPI cards and charts stay in sync.

  // SAM-1275: an unbounded list passed Lambda's 6 MB response for the tenant Front Desk (every pass of the tenant,
  // 403 at the gateway). The range is still applied exactly on the client; the server only gets a lower bound, a
  // day earlier so a timezone difference can never drop a pass of the first day.
  if (filters.startDate) {
    const from = new Date(filters.startDate);
    if (!Number.isNaN(from.getTime())) {
      from.setDate(from.getDate() - 1);
      params.start_date = from.toISOString().slice(0, 10);
    }
  }

  if (filters.host_user_id) {
    params.host_user_id = filters.host_user_id;
  }

  if (filters.estate_id) {
    params.estate_id = filters.estate_id;
  }
  
  return useQuery<BusinessAccessResponse>({
    queryKey: ['dashboard-data', filters, combined],
    enabled: options.enabled !== false,
    queryFn: async () => {
      try {
        // SAM-1311: the estate group roles' passes are residential invitations, which /business_access does not
        // have (404 "No data found", Dashboard at 0). /combined has both; its window is an overlap test on whole
        // days and the exact range is still applied on the client below.
        if (combined) {
          const day = (iso?: string) => {
            const d = iso ? new Date(iso) : null;
            return d && !Number.isNaN(d.getTime()) ? toDayParam(d) : undefined;
          };
          const rows = await fetchCombinedAll<BusinessAccess>({
            date_from: day(filters.startDate),
            date_to: day(filters.endDate),
            estate_id: filters.estate_id,
            host_user_id: filters.host_user_id,
          });
          const filtered = applyDashboardFilters(rows, filters);
          return { data: filtered, page: 1, total_pages: 1, total_elements: filtered.length } as BusinessAccessResponse;
        }
        const response = await businessAccessApi<BusinessAccessResponse>({
          url: '',  // Base URL
          method: 'GET',
          params,
        });
        
        // Aplicar filtros del lado del cliente
        // (SAM-217 date-range note lives on applyDashboardFilters above.)
        const filteredData = applyDashboardFilters(response.data || [], filters);

        return {
          ...response,
          data: filteredData,
          total_elements: filteredData.length,
        };
      } catch (error) {
        // SAM-216: surface the failure so React Query reports `isError`, which
        // drives the Dashboard "Live / Offline" connection indicator. Consumers
        // already guard against undefined data (`dashboardData?.data || []`), so
        // a thrown error degrades to an empty dataset without crashing.
        throw error;
      }
    },
    // Configuración para manejo de errores
    retry: false, // No reintentar si hay un error
    staleTime: 30000, // Considerar datos frescos por 30 segundos
  });
}; 